import React, { useContext, useEffect, useCallback, useState, useMemo, useRef } from 'react'
import { AppBar, Toolbar, FormControl, Select, MenuItem, SelectChangeEvent, Box, Button, LinearProgress, Typography, Divider, ListSubheader, TextField, IconButton, Paper, Popper, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { canAccessSkySocialMedia } from '../utils/skyNav'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import axios from 'axios'

import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useSnackbarTopOffset from '../hooks/useSnackbarTopOffset'
import useRecentTables, { RecentTable } from '../hooks/useRecentTables'
import { LoadingContext } from '../contexts/loading'
import { SnackbarContext, snackbarPlacement } from '../contexts/snackbar'
import { UnsavedChangesContext } from '../contexts/unsavedChanges'
import { PageJobProvider, usePageJobState } from '../contexts/pageJob'
import UnsavedChangesDialog from '../components/system/UnsavedChangesDialog'
import ImpersonationBanner, { IMPERSONATION_BANNER_HEIGHT } from '../components/system/ImpersonationBanner'
import NotificationBell from '../components/system/NotificationBell'
import AdminNavDrawer from '../components/system/AdminNavDrawer'
import DemoBadge from '../components/system/DemoBadge'
import { useAiAccess } from '../hooks/useAiAccess'
import useBudgetBanner from '../hooks/useBudgetBanner'
import BudgetBanner from '../components/system/BudgetBanner'
import { useHelp } from '../help/HelpProvider'
import { HEADER_RULE_PX } from '../theme'
import { INSIGHTS_DRAWER_CSS_WIDTH } from '../components/chat/ChatDrawer'

// Configure which customers are considered demo customers
const DEMO_CUSTOMERS = ['bprdemo', 'bprtesting']

// Pill-style selector (used for customer + job/spec in the header)
const pillSelectSx = {
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '999px',
    height: 36,
    fontSize: '0.875rem',
    paddingLeft: '16px',
    paddingRight: '4px',
    transition: 'background-color 0.15s ease',
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
    '&.Mui-focused': { backgroundColor: 'rgba(255,255,255,0.12)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '0 32px 0 0 !important',
    minHeight: 'unset',
  },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.55)', right: 8 },
} as const

const pillLabelSx = {
  color: 'rgba(255,255,255,0.55)',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  mr: 1,
}

const pillValueSx = {
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 500,
  whiteSpace: 'nowrap' as const,
}

// The locked Job pill: same shape as the Select pill, minus the caret, the
// hover lift and the pointer. A dashboard is bound to one job (#961), so the
// job is context to read, not a control — but hiding it entirely left the
// header saying nothing about which job's numbers were on screen.
const pillStaticSx = {
  display: 'flex',
  alignItems: 'center',
  height: 36,
  px: 2,
  borderRadius: '999px',
  backgroundColor: 'rgba(255,255,255,0.06)',
}

// Responsive nav button base styles
const navButtonSx = (active: boolean) => ({
  color: active ? 'white' : 'rgba(255,255,255,0.65)',
  bgcolor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  fontWeight: active ? 600 : 400,
  textTransform: 'none' as const,
  borderRadius: '6px',
  px: { xs: 1, md: 1.25, lg: 1.5 },
  fontSize: { xs: '0.75rem', md: '0.8125rem', lg: '0.875rem' },
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap' as const,
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' },
})

interface LayoutProps {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const account = useAccount()
  const router = useRouter()
  const help = useHelp()
  const { loading, progressTitle, progressTotal, progressCurrent } = useContext(LoadingContext)
  const { snackbar, setSnackbar } = useContext(SnackbarContext)
  const snackbarTopOffset = useSnackbarTopOffset()
  const { pendingNavigation, confirmLeave, cancelLeave } = useContext(UnsavedChangesContext)
  const pageJobState = usePageJobState()
  const [customerSearchText, setCustomerSearchText] = useState('')
  const [jobSearchText, setJobSearchText] = useState('')
  // The admin menu, slid in from the right by the top bar's Admin button — the
  // place the old three-dots pane used (Matt + Kai, 30 Sep 2026).
  const [adminDrawerOpen, setAdminDrawerOpen] = useState(false)
  const theme = useTheme()
  const isWideScreen = useMediaQuery(theme.breakpoints.up('md'))
  const [tablesMenuOpen, setTablesMenuOpen] = useState(false)
  const tablesButtonRef = useRef<HTMLButtonElement>(null)
  const tablesMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recentTables = useRecentTables(account.customer?.name || '', account.job?.name || '', account.toc)

  const isRouteActive = (name: string) => router.name == name
  const doesRouteStartWith = (name: string) => router.name?.startsWith(name)

  const userPermissions = account.user?.domain_permissions
  const isManager = account.user?.role_permissions.isManager
  // The Agent entry (Kai, 30 Sep 2026, access audit A07-01): a Manager always
  // sees it; with the customer's AI access enforced, the level ladder decides
  // (level 3, ignoring the budget: a used budget greys the composer inside);
  // with it off, the domain config decides as before.
  const aiAccess = useAiAccess(account.customer?.name || undefined)
  const budgetBanner = useBudgetBanner(account.customer?.name || undefined)
  const showAgent = isManager ? true : aiAccess.enforced ? aiAccess.levelAllows('agent_sessions') : !!userPermissions?.canAccessAgent

  const handleCustomerChange = async (event: SelectChangeEvent<string>) => {
    // Don't change customer/job if we're on a TikTok page
    if (router.name?.startsWith('tiktok-')) {
      return
    }
    account.setCustomerID(event.target.value)
  }

  const handleJobChange = async (event: SelectChangeEvent<string>) => {
    // Don't change customer/job if we're on a TikTok page
    if (router.name?.startsWith('tiktok-')) {
      return
    }
    account.setJobID(event.target.value)
  }

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return
    setSnackbar(undefined)
  }

  const isTikTokPage = router.name?.startsWith('tiktok-')

  const customerElements: JSX.Element[] = []
  const customers = account.user?.session_info?.sessionCusts || []

  // Separate customers into demo and non-demo
  const regularCustomers = customers.filter(customer =>
    !DEMO_CUSTOMERS.includes(customer.name || '')
  )
  const demoCustomers = customers.filter(customer =>
    DEMO_CUSTOMERS.includes(customer.name || '')
  )

  // Sort both groups alphabetically by display name
  const sortByDisplayName = (a: any, b: any) =>
    (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '')

  regularCustomers.sort(sortByDisplayName)
  demoCustomers.sort(sortByDisplayName)

  // Filter customers based on search text
  const filteredRegularCustomers = useMemo(() => {
    if (!customerSearchText) return regularCustomers
    const searchLower = customerSearchText.toLowerCase()
    return regularCustomers.filter(customer =>
      (customer.displayName || '').toLowerCase().includes(searchLower) ||
      (customer.name || '').toLowerCase().includes(searchLower)
    )
  }, [customerSearchText, regularCustomers])

  const filteredDemoCustomers = useMemo(() => {
    if (!customerSearchText) return demoCustomers
    const searchLower = customerSearchText.toLowerCase()
    return demoCustomers.filter(customer =>
      (customer.displayName || '').toLowerCase().includes(searchLower) ||
      (customer.name || '').toLowerCase().includes(searchLower)
    )
  }, [customerSearchText, demoCustomers])

  filteredRegularCustomers.forEach((customer) => {
    customerElements.push(
      <MenuItem key={customer.name || ''} value={customer.name || ''}>
        {customer.displayName || customer.name}
      </MenuItem>
    )
  })
  if (filteredRegularCustomers.length > 0 && filteredDemoCustomers.length > 0) {
    customerElements.push(<Divider key="divider" />)
  }
  filteredDemoCustomers.forEach((customer) => {
    customerElements.push(
      <MenuItem key={customer.name || ''} value={customer.name || ''}>
        {customer.displayName || customer.name}
      </MenuItem>
    )
  })

  // Prepare and filter jobs
  const jobs = account.customer?.sessionJobs || []
  const sortedJobs = [...jobs].sort((a, b) =>
    (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '')
  )

  const filteredJobs = useMemo(() => {
    if (!jobSearchText) return sortedJobs
    const searchLower = jobSearchText.toLowerCase()
    return sortedJobs.filter(job =>
      (job.displayName || '').toLowerCase().includes(searchLower) ||
      (job.name || '').toLowerCase().includes(searchLower)
    )
  }, [jobSearchText, sortedJobs])

  // The top bar (Matt + Kai, 30 Sep 2026). There is no three-dots pane any
  // more: what it held is either here or in the Admin menu (AdminDrawer's
  // NAV_GROUPS), which the Admin button after the bell slides in from the right.
  const headerNavItems = [
    // Agent leads and Home is gone (Matt + Adam, 9 Sep 2026): the agent start
    // screen is the front door, and the job home page it replaced stays
    // reachable by route only.
    { label: 'Agent', visible: showAgent, onClick: () => router.navigate('agent', { customer: account.customer?.name || '' }), active: !!doesRouteStartWith('agent') },
    { label: 'Tables', visible: true, onClick: () => account.openPage('tables'), active: isRouteActive('tables') },
    { label: 'Variables', visible: true, onClick: () => account.openPage('variables'), active: isRouteActive('variables') || isRouteActive('variables-data') || isRouteActive('variables-tools') },
    // T6.1: gated on the Sky customer AND job, not on the domain-derived
    // permission alone — that permission says nothing about the JOB, so a Sky
    // user on a non-TikTok job was shown a tab leading to another job's
    // dashboard. Points at the chooser, which is what survives Track 6.
    { label: 'Social Media', visible: canAccessSkySocialMedia(userPermissions, account.customer?.name, account.job?.name), onClick: () => account.openPage('sky-social-media'), active: isRouteActive('sky-social-media') },
    { label: 'Files', visible: true, onClick: () => account.openPage('files'), active: isRouteActive('files') },
    { label: 'DataViz', visible: !!userPermissions?.canAccessDashboards, onClick: () => router.navigate('dashboards', { customer: account.customer?.name || '' }), active: !!doesRouteStartWith('dashboard') },
    // Audit A07-03: Theme AI reads its OWN domain flag. It used to read
    // canAccessAutocoder, so no Sky user ever saw it although sky.uk holds
    // canAccessThemeAI and the code-frame agent is a level-2 feature.
    { label: 'Theme AI', visible: !!userPermissions?.canAccessThemeAI, onClick: () => account.openPage('theme-ai-tasks'), active: !!doesRouteStartWith('theme-ai') },
  ]

  // On an admin page a wide screen already shows the admin menu as the
  // page's own persistent drawer, so the button marks where you are instead
  // of opening a second copy of the same list.
  const onAdminPage = !!doesRouteStartWith('admin')
  const handleAdminClick = () => {
    if (onAdminPage && isWideScreen) return
    setAdminDrawerOpen(open => !open)
  }

  const showCustomerSelect = !!account.user && isManager && !isTikTokPage && !router.meta?.hideCustomerSelect
  // Three states, not two: pick a job, read the job you are locked to, or no
  // job at all. `lockJobSelect` is the dashboard pages (#961 binds a dashboard
  // to one job); `hideJobSelect` stays for pages where a job means nothing,
  // like Admin and the logged-out shared view.
  // Locked pages show the job the PAGE owns, not the one in the session — see
  // contexts/pageJob. No page job (a legacy pre-#961 dashboard) means no pill:
  // showing account.job there would be a guess, and usually a wrong one.
  const lockedJob = jobs.find(j => j.name === pageJobState.pageJob)
  const lockJobSelect = !!account.user && !!router.meta?.lockJobSelect && !!pageJobState.pageJob
  const showJobSelect = !!account.user && !router.meta?.hideJobSelect && !router.meta?.lockJobSelect
  const selectedCustomer = customers.find(c => c.name === account.customer?.name)
  const selectedJob = jobs.find(j => j.name === account.job?.name)

  // The job the Demo badge asks about: the one the header shows, if one.
  const badgeJob = lockJobSelect
    ? (pageJobState.pageJobs.length > 1 ? undefined : pageJobState.pageJob)
    : showJobSelect ? account.job?.name : undefined

  const headerSelectors = (showCustomerSelect || showJobSelect || lockJobSelect) && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.15)' }}>
      {showCustomerSelect && (
        <FormControl size="small" sx={pillSelectSx}>
          <Select
            value={account.customer?.name || ''}
            onChange={handleCustomerChange}
            onClose={() => setCustomerSearchText('')}
            displayEmpty
            inputProps={{ 'aria-label': 'Customer' }}
            renderValue={() => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="span" sx={pillLabelSx}>Customer</Typography>
                <Typography component="span" sx={pillValueSx}>
                  {selectedCustomer?.displayName || selectedCustomer?.name || '—'}
                </Typography>
              </Box>
            )}
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                sx: { '& .MuiMenuItem-root': { fontSize: '0.8125rem', py: 0.5 }, '& .MuiListSubheader-root': { fontSize: '0.8125rem' } },
                onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
                  if ((e.target as HTMLElement).closest('.customer-search-field')) {
                    e.stopPropagation()
                  }
                }
              }
            }}
          >
            <ListSubheader className="customer-search-field">
              <TextField
                size="small"
                autoFocus
                placeholder="Search customers..."
                fullWidth
                value={customerSearchText}
                onChange={(e) => setCustomerSearchText(e.target.value)}
                onKeyDown={(e) => { e.stopPropagation() }}
                sx={{ my: 1 }}
              />
            </ListSubheader>
            { customerElements }
          </Select>
        </FormControl>
      )}
      {lockJobSelect && (
        // #1229 C1: a multi-job dashboard's pill reads "Multi" — it is not
        // one job any more; Settings lists the members (tooltip too).
        // An agent session locks the pill the same way (#1347); its job set
        // is managed in the session's Jobs tab, so the hint says so.
        <Tooltip title={pageJobState.pageJobs.length > 1
          ? (router.meta?.lockJobOwner === 'session'
            ? `Session jobs: ${pageJobState.pageJobs.map(j => jobs.find(x => x.name === j)?.displayName || j).join(' · ')} — manage them in the Jobs tab`
            : `Multi-job dashboard: ${pageJobState.pageJobs.map(j => jobs.find(x => x.name === j)?.displayName || j).join(' · ')} — manage the jobs in the dashboard's Settings`)
          : (router.meta?.lockJobOwner === 'session'
            ? 'The job this session works on — change it in the Jobs tab'
            : 'This dashboard is tied to this job — open another dashboard to change job')}>
          <Box sx={pillStaticSx} data-testid="locked-job-pill">
            <Typography component="span" sx={pillLabelSx}>Job</Typography>
            <Typography component="span" sx={pillValueSx}>
              {pageJobState.pageJobs.length > 1
                ? 'Multi'
                : (lockedJob?.displayName || lockedJob?.name || pageJobState.pageJob)}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {showJobSelect && (
        <FormControl size="small" sx={pillSelectSx}>
          <Select
            value={account.job?.name || ''}
            onChange={handleJobChange}
            onClose={() => setJobSearchText('')}
            displayEmpty
            inputProps={{ 'aria-label': 'Job' }}
            renderValue={() => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="span" sx={pillLabelSx}>Job</Typography>
                <Typography component="span" sx={pillValueSx}>
                  {selectedJob?.displayName || selectedJob?.name || '—'}
                </Typography>
              </Box>
            )}
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                sx: { '& .MuiMenuItem-root': { fontSize: '0.8125rem', py: 0.5 }, '& .MuiListSubheader-root': { fontSize: '0.8125rem' } },
                onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
                  if ((e.target as HTMLElement).closest('.job-search-field')) {
                    e.stopPropagation()
                  }
                }
              }
            }}
          >
            <ListSubheader className="job-search-field">
              <TextField
                size="small"
                autoFocus
                placeholder="Search jobs..."
                fullWidth
                value={jobSearchText}
                onChange={(e) => setJobSearchText(e.target.value)}
                onKeyDown={(e) => { e.stopPropagation() }}
                sx={{ my: 1 }}
              />
            </ListSubheader>
            {filteredJobs.map((job) => (
              <MenuItem key={job.name || ''} value={job.name || ''}>
                {job.displayName || job.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* A demo story's job says so (Admin → Demos); nothing on any other job. */}
      {badgeJob && <DemoBadge customer={account.customer?.name ?? undefined} job={badgeJob} />}
    </Box>
  )

  const loggedInContent = account.user && (
    <Box sx={{ display: 'flex', gap: { xs: 0.25, md: 0.5, lg: 0.75 }, alignItems: 'center' }}>
      {headerNavItems.filter(item => item.visible).map(item => {
        if (item.label === 'Tables') {
          const hasRecent = recentTables.tables.length > 0
          return (
            <Box
              key={item.label}
              onMouseEnter={() => {
                if (tablesMenuTimeoutRef.current) clearTimeout(tablesMenuTimeoutRef.current)
                setTablesMenuOpen(true)
              }}
              onMouseLeave={() => {
                tablesMenuTimeoutRef.current = setTimeout(() => setTablesMenuOpen(false), 150)
              }}
              sx={{ position: 'relative' }}
            >
              <Button
                ref={tablesButtonRef}
                color="inherit"
                onClick={() => {
                  setTablesMenuOpen(false)
                  item.onClick()
                }}
                sx={navButtonSx(item.active)}
              >
                {item.label}
              </Button>
              <Popper
                open={tablesMenuOpen}
                anchorEl={tablesButtonRef.current}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  elevation={8}
                  sx={{ mt: 0.5, minWidth: 280, maxWidth: 360, py: 1 }}
                  onMouseEnter={() => {
                    if (tablesMenuTimeoutRef.current) clearTimeout(tablesMenuTimeoutRef.current)
                  }}
                  onMouseLeave={() => {
                    tablesMenuTimeoutRef.current = setTimeout(() => setTablesMenuOpen(false), 150)
                  }}
                >
                  {hasRecent && (
                    <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem', display: 'block' }}>
                      Recent Tables
                    </Typography>
                  )}
                  {recentTables.tables.slice(0, 10).map(t => (
                    <Box
                      key={t.path}
                      onClick={() => {
                        setTablesMenuOpen(false)
                        account.openPage('tables', { table: t.path.replace(/\.cbt$/, '') })
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <TableChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          recentTables.toggleStar(t.path)
                        }}
                        aria-label={t.starred ? `Unstar ${t.name}` : `Star ${t.name}`}
                        sx={{ p: 0.25, color: t.starred ? 'secondary.main' : 'text.secondary', opacity: t.starred ? 1 : 0.4, '&:hover': { opacity: 1 } }}
                      >
                        {t.starred ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Box>
                  ))}
                  {hasRecent && <Divider sx={{ my: 0.5 }} />}
                  <Box
                    onClick={() => {
                      setTablesMenuOpen(false)
                      item.onClick()
                    }}
                    sx={{
                      px: 2,
                      py: 0.75,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      View all tables
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => {
                      setTablesMenuOpen(false)
                      account.openPage('batch-generate')
                    }}
                    sx={{
                      px: 2,
                      py: 0.75,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Batch Generate
                    </Typography>
                  </Box>
                </Paper>
              </Popper>
            </Box>
          )
        }
        return (
          <Button
            key={item.label}
            color="inherit"
            onClick={item.onClick}
            sx={navButtonSx(item.active)}
          >
            {item.label}
          </Button>
        )
      })}
      {account.customer?.name && <NotificationBell customer={account.customer.name} />}
      {/* Help (System context PR B): the text help agent for the section on
          screen, on every page that does not hide the assistant. */}
      {help?.available && (
        <Button
          color="inherit"
          onClick={help.open ? help.handleClose : help.handleOpen}
          aria-expanded={help.open}
          sx={navButtonSx(help.open)}
        >
          Help
        </Button>
      )}
      {/* Admin is open to everyone, as the three-dots item it replaces was:
          the Account group (Change Password, Your AI access, Alerts) is for
          every user, and each other group gates itself. */}
      <Button
        color="inherit"
        onClick={handleAdminClick}
        aria-expanded={adminDrawerOpen}
        sx={navButtonSx(onAdminPage || adminDrawerOpen)}
      >
        Admin
      </Button>
      <Button
        color="inherit"
        onClick={() => account.onLogout()}
        sx={navButtonSx(false)}
      >
        Log out
      </Button>
    </Box>
  )
  const hideTopBar = !!router.meta?.hideTopBar
  // A page that draws its own header, nav and main landmarks (the public
  // /help docs) is wrapped in a plain box, so there is one <main>, its own.
  const ownLandmarks = !!router.meta?.ownLandmarks

  const bannerOffset = account.isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0
  // The Help pane makes room beside the page instead of covering it, so a
  // control it outlines (Discard at the right end of the preview bar) and the
  // header's own nav stay visible (Adam, 14 Oct, tour on staging).
  const helpRoom = { transition: 'margin-right 200ms ease, width 200ms ease' }
  const helpShown = !!help?.available && !!help?.open

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ImpersonationBanner />
      {!hideTopBar && <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{ top: `${bannerOffset}px`, ...helpRoom, left: 0, right: 'auto', width: helpShown ? `calc(100% - ${INSIGHTS_DRAWER_CSS_WIDTH})` : '100%' }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            backgroundColor: 'primary.main',
            borderBottom: `${HEADER_RULE_PX}px solid`,
            borderColor: 'secondary.main',
            boxShadow: 'inset 0 -4px 6px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/img/platinum-logo.png"
              alt="Bayes Price Logo"
              style={{ height: '32px', marginRight: '16px', filter: 'brightness(0) invert(1)' }}
            />
            {headerSelectors}
          </Box>
          {loggedInContent}
        </Toolbar>
      </AppBar>}
      {account.user && (
        <AdminNavDrawer open={adminDrawerOpen} onClose={() => setAdminDrawerOpen(false)} />
      )}
      <Box id="main-content" component={ownLandmarks ? 'div' : 'main'} sx={{ flexGrow: 1, mt: `${(hideTopBar ? 0 : 64) + bannerOffset}px`, backgroundColor: 'background.default', overflow: 'hidden', ...helpRoom, marginRight: helpShown ? INSIGHTS_DRAWER_CSS_WIDTH : 0 }}>
        {account.user && <BudgetBanner banner={budgetBanner.banner} onDismiss={budgetBanner.handleDismiss} />}
        <PageJobProvider value={pageJobState}>
          {children}
        </PageJobProvider>
      </Box>

      {/* Global Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {/* Show progress title if it exists */}
          {progressTitle && (
            <Typography variant="h6" color="inherit" sx={{ textAlign: 'center' }}>
              {progressTitle}
            </Typography>
          )}

          {/* Show progress bar only if total is greater than 0 */}
          {progressTotal > 0 ? (
            <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(progressCurrent / progressTotal) * 100}
                sx={{
                  width: '100%',
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: 'secondary.main'
                  }
                }}
              />
              <Typography variant="body2" color="inherit">
                {progressCurrent} of {progressTotal}
              </Typography>
            </Box>
          ) : (
            // Show circular progress when no specific progress is tracked
            <CircularProgress color="inherit" />
          )}
        </Box>
      </Backdrop>

      {/* Global Snackbar */}
      <Snackbar
        open={!!snackbar}
        // autoHideMs toasts (e.g. login) close themselves via their own
        // unconditional timer in the snackbar context — MUI's own
        // autoHideDuration pauses on hover/blur, so it's disabled here to
        // avoid that mechanism lingering around the same toast.
        autoHideDuration={snackbar?.autoHideMs ? undefined : 6000}
        onClose={handleSnackbarClose}
        {...snackbarPlacement(snackbar?.anchor || 'top', snackbarTopOffset)}
      >
        {/* Render Alert only when snackbar has data */}
        {snackbar && (
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              bgcolor: snackbar.color,
              color: 'white',
              border: `1px solid #333333`, // Add border with 85% opacity of background color
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={pendingNavigation !== null}
        onStay={cancelLeave}
        onLeave={confirmLeave}
      />

    </Box>
  )
}

export default Layout
